// 2026-10-04 — see ADMIN_RCM_CONSOLIDATION_PLAN.md. Visual language brought
// in line with PageShell.js's real treatment (dot-grid texture, ambient
// animated blobs, gold-bordered sticky navbar) — the first version of this
// file had a much thinner inline header, a real gap flagged directly.
// PageShell itself isn't reused verbatim: its back-link always targets "/"
// and bundles NavUserMenu, both staff-context assumptions that don't apply
// to this single-hardcoded-admin page — same visual language, its own
// header markup.
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, Tabs, Tab, CircularProgress, IconButton, Tooltip, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";
import RcmCharts from "./RcmCharts";
import RcmPatientPanel from "./RcmPatientPanel";
import RcmClinicianPanel from "./RcmClinicianPanel";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const B = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e", goldLight: "#dfc08a" };

const DOT_GRID_DARK  = "radial-gradient(circle, rgba(255,255,255,0.04) 1.2px, transparent 1.2px)";

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

export default function RcmDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Visual-consistency pass — page background must match every other admin
  // page's canonical light-grey (AdminDashboard.js's "#F7F7F7"), not this
  // page's own blue-tinted value — reported directly ("the background is
  // blue color, but in the others it's grey").
  const bg = isDark ? "#080d1a" : "#F7F7F7";
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  // 2026-10-15 fix (client-facing audit-log completeness sweep finding) —
  // a genuine fetch failure here used to silently set stats to null,
  // which is indistinguishable from "stats just hasn't loaded" —
  // RcmCharts (below) already guards `if (!stats) return null`, so this
  // rendered as a silent BLANK charts section (not a crash — corrected
  // after a verifier pass caught this comment overstating the old
  // behavior), with no indication to an admin that a real fetch failure,
  // not an empty dataset, caused it. Now a genuine error gets a clear,
  // recoverable error state instead.
  const [statsError, setStatsError] = useState(false);

  const loadStats = () => {
    setStatsLoading(true);
    setStatsError(false);
    axios.get(`${API}/api/admin/rcm/stats`, { headers: authHeaders() })
      .then((res) => setStats(res.data))
      .catch(() => setStatsError(true))
      .finally(() => setStatsLoading(false));
  };

  useEffect(() => { loadStats(); }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, position: "relative", overflow: "hidden", pb: 8 }}>
      {/* Dot-grid texture + ambient colour blobs — same treatment as
          PageShell.js, but these were ALSO part of what made this page read
          as "blue" next to the flat #F7F7F7 pages (navy dots/blobs over a
          grey base still tint it). Dark mode keeps its own moodier
          treatment; light mode (what was actually being compared side by
          side) drops both so the base color reads as the same flat grey
          every other admin page uses. */}
      {isDark && (
        <>
          <Box sx={{
            position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
            backgroundImage: DOT_GRID_DARK,
            backgroundSize: "28px 28px", opacity: 0.85,
          }} />
          <Box sx={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
            {[
              { top: "-5%", left: "-4%", size: 480, color: "rgba(0,53,122,0.18)" },
              { top: "60%", right: "-5%", size: 360, color: "rgba(201,169,110,0.08)" },
            ].map((b, i) => (
              <motion.div key={i} style={{
                position: "absolute", top: b.top, left: b.left, right: b.right,
                width: b.size, height: b.size, borderRadius: "50%", background: b.color, filter: "blur(90px)",
              }}
                animate={{ scale: [1, 1.08, 1], opacity: [0.75, 1, 0.75] }}
                transition={{ duration: 10 + i * 3, repeat: 2, delay: i * 2, ease: "easeInOut" }} />
            ))}
          </Box>
        </>
      )}

      {/* Navbar — matches PageShell.js's gold-bordered sticky navbar */}
      <motion.div initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={{ position: "sticky", top: 0, zIndex: 20 }}>
        <Box sx={{
          bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.96)",
          backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(201,169,110,0.2)",
          boxShadow: "0 4px 28px rgba(0,31,77,0.2)",
        }}>
          <Box sx={{ height: 2, background: `linear-gradient(90deg, transparent, ${B.gold} 30%, ${B.goldLight} 50%, ${B.gold} 70%, transparent)` }} />
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 60, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box onClick={() => navigate("/admin")}
                sx={{
                  display: "flex", alignItems: "center", gap: 1, color: "rgba(255,255,255,0.55)", fontSize: 14.5,
                  fontFamily: "'Inter', sans-serif", cursor: "pointer", borderRadius: "8px", px: 1.5, py: 0.7,
                  transition: "all 0.2s", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                }}>
                <ArrowBackIcon sx={{ fontSize: 15 }} />
                <span>Admin</span>
              </Box>
              {/* 2026-10-04 fix — same real bug fixed in RequisitionConversion.js's
                  TopBar: MUI's sx prop treats a unitless width between 0-1
                  as a PERCENTAGE, not px — "width: 1" rendered as a giant
                  block instead of a 1px divider. */}
              <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.12)" }} />
              {/* 2026-10-08 fix — same navbar family as AdminDashboard.js /
                  AuditDashboard.js / UserDashboard.js / IdentityLinking.js,
                  all of which carry the Labarium logo here; this was the
                  one navbar in the family missing it. */}
              <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
              <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.12)" }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.2em", color: B.gold, fontFamily: "'Inter', sans-serif" }}>
                ADMIN
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#fff", fontFamily: "'Playfair Display', serif" }}>
                RCM Dashboard
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ThemeToggle />
              <Tooltip title="Log out">
                <IconButton size="small" onClick={() => { localStorage.removeItem("admin_token"); navigate("/admin-login"); }}
                  sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </motion.div>

      {/* Content width matches AdminDashboard.js's canonical
          Container maxWidth="md" exactly — this page previously used a
          plain Box with maxWidth:1400, which is exactly the "RCM/Users are
          different widths" gap reported directly. */}
      <Container maxWidth="md" sx={{ py: 4, position: "relative", zIndex: 10 }}>
        {statsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: B.navy }} /></Box>
        ) : statsError ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ fontSize: "0.85rem", color: "#c0392b", mb: 1.5 }}>
              Couldn't load RCM stats. This is a real error, not an empty result — try again.
            </Typography>
            <Button size="small" variant="outlined" onClick={loadStats} sx={{ textTransform: "none" }}>Retry</Button>
          </Box>
        ) : (
          <RcmCharts stats={stats} />
        )}

        <Box sx={{
          borderRadius: "16px", overflow: "hidden", border: `1px solid ${isDark ? "rgba(201,169,110,0.18)" : "rgba(0,53,122,0.12)"}`,
          bgcolor: isDark ? "rgba(17,24,39,0.6)" : "#fff", boxShadow: isDark ? "none" : "0 2px 16px rgba(0,31,77,0.05)",
        }}>
          <Box sx={{
            px: 3, pt: 1.5, background: isDark
              ? "linear-gradient(135deg, rgba(0,53,122,0.25), rgba(201,169,110,0.08))"
              : "linear-gradient(135deg, rgba(0,53,122,0.05), rgba(201,169,110,0.05))",
            borderBottom: `1px solid ${isDark ? "rgba(201,169,110,0.15)" : "rgba(0,53,122,0.1)"}`,
          }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
              minHeight: 44,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 15, minHeight: 44, color: isDark ? "#94a3b8" : "#64748b" },
              "& .Mui-selected": { color: `${isDark ? B.goldLight : B.navy} !important` },
              "& .MuiTabs-indicator": { bgcolor: isDark ? B.gold : B.navy },
            }}>
              <Tab label="Patients" />
              <Tab label="Clinicians" />
            </Tabs>
          </Box>
          <Box sx={{ p: 3 }}>
            {tab === 0 ? <RcmPatientPanel isDark={isDark} /> : <RcmClinicianPanel isDark={isDark} />}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
